import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { updatePassword, updateEmail, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";

const FAQ = [
  { q: "How do I deposit funds?", a: "Go to the Deposit page and choose your preferred method — Bank Transfer or Crypto ETH. For bank transfer, send to our account and upload proof. For crypto, send ETH to our wallet address. Admin will confirm within 1x24 hours." },
  { q: "How long does withdrawal take?", a: "Withdrawals are processed within 1-3 business days after admin approval. Make sure your KYC documents are verified for faster processing." },
  { q: "What is leverage?", a: "Leverage allows you to control a larger position with a smaller amount. For example, 1:2000 leverage means $1 controls $2000 worth of assets. Higher leverage means higher potential profit AND higher potential loss." },
  { q: "What is a lot?", a: "A lot is the standard unit of trading. 1 lot of Gold = 100 oz. You can trade as small as 0.01 lot (micro lot). Your required margin depends on lot size, contract size, entry price, and your leverage setting." },
  { q: "How is P&L calculated?", a: "P&L = ((Close Price - Entry Price) / Entry Price) × Position Value. For SELL trades, the formula is reversed. P&L can be positive (profit) or negative (loss)." },
  { q: "Why do I need KYC verification?", a: "KYC (Know Your Customer) is required by financial regulations to verify your identity and prevent fraud. Upload your ID, proof of residence, and bank statement to unlock full trading features." },
  { q: "Is my money safe?", a: "Troy Trade Investment operates under PT. Artemis Bosphora Arthagena, registered in Seychelles (Reg. No. 8426105-4) with license HE433420. All funds are managed with strict security protocols." },
  { q: "What assets can I trade?", a: "We offer 20+ assets including Gold (XAU/USD), Silver, Oil, Bitcoin, Ethereum, major Forex pairs (EUR/USD, GBP/USD, etc.), and top Stocks (AAPL, TSLA, NVDA, etc.)." },
  { q: "How do I contact support?", a: "Email us at ptartemisbosphoraarthagena@gmail.com or use the support form below. Our team responds within 24 hours on business days." },
];

const LEVERAGE_OPTIONS = [
  { label: "1:100", value: 100 },
  { label: "1:200", value: 200 },
  { label: "1:500", value: 500 },
  { label: "1:1000", value: 1000 },
  { label: "1:2000", value: 2000 },
  { label: "Unlimited", value: 99999 },
];

export default function Settings({ user }) {
  const [tab, setTab] = useState("account");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [supportName, setSupportName] = useState("");
  const [supportEmail, setSupportEmail] = useState(user.email || "");
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMsg, setSupportMsg] = useState("");
  const [supportSent, setSupportSent] = useState(false);
  const [selectedLeverage, setSelectedLeverage] = useState(100);
  const [savingLeverage, setSavingLeverage] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists() && snap.data().leverage) {
        setSelectedLeverage(snap.data().leverage);
      }
    };
    fetchSettings();
  }, [user]);

  const handleSaveLeverage = async () => {
    setSavingLeverage(true); setMsg("");
    try {
      await setDoc(doc(db, "users", user.uid), { leverage: selectedLeverage }, { merge: true });
      setMsg("✓ Leverage setting saved! All future trades will use " + LEVERAGE_OPTIONS.find(l => l.value === selectedLeverage)?.label);
      setTimeout(() => setMsg(""), 4000);
    } catch (e) {
      setMsg("Error: " + e.message);
    }
    setSavingLeverage(false);
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) { setMsg("Please fill all fields"); return; }
    if (newPassword !== confirmPassword) { setMsg("New passwords do not match"); return; }
    if (newPassword.length < 6) { setMsg("Password must be at least 6 characters"); return; }
    setLoading(true); setMsg("");
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
      setMsg("✓ Password changed successfully!");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (e) {
      setMsg("Error: " + (e.code === "auth/wrong-password" ? "Current password is incorrect" : e.message));
    }
    setLoading(false);
  };

  const handleChangeEmail = async () => {
    if (!newEmail || !emailPassword) { setMsg("Please fill all fields"); return; }
    setLoading(true); setMsg("");
    try {
      const credential = EmailAuthProvider.credential(user.email, emailPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updateEmail(auth.currentUser, newEmail);
      setMsg("✓ Email changed successfully!");
      setNewEmail(""); setEmailPassword("");
    } catch (e) {
      setMsg("Error: " + e.message);
    }
    setLoading(false);
  };

  const handleSupportSubmit = () => {
    if (!supportName || !supportSubject || !supportMsg) { setMsg("Please fill all fields"); return; }
    const mailtoLink = `mailto:ptartemisbosphoraarthagena@gmail.com?subject=${encodeURIComponent("[Troy Trade Support] " + supportSubject)}&body=${encodeURIComponent("Name: " + supportName + "\nEmail: " + supportEmail + "\n\n" + supportMsg)}`;
    window.open(mailtoLink);
    setSupportSent(true);
    setMsg("✓ Support email opened! Please send it from your email client.");
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-title">Settings & Support</div>
      </div>

      <div className="dep-tabs">
        <div className={`dep-tab ${tab === "leverage" ? "active" : ""}`} onClick={() => { setTab("leverage"); setMsg(""); }}>
          ⚡ Leverage
        </div>
        <div className={`dep-tab ${tab === "account" ? "active" : ""}`} onClick={() => { setTab("account"); setMsg(""); }}>
          ⚙️ Account
        </div>
        <div className={`dep-tab ${tab === "faq" ? "active" : ""}`} onClick={() => { setTab("faq"); setMsg(""); }}>
          ❓ FAQ
        </div>
        <div className={`dep-tab ${tab === "support" ? "active" : ""}`} onClick={() => { setTab("support"); setMsg(""); }}>
          💬 Support
        </div>
      </div>

      <div className="settings-content">

        {tab === "leverage" && (
          <div style={{maxWidth:"560px"}}>
            <div className="settings-card">
              <div className="settings-card-title">⚡ Leverage Setting</div>
              <div style={{fontSize:"12px", color:"#5A6480", lineHeight:"1.6", marginBottom:"8px"}}>
                Select your preferred leverage. This will apply to all your trades automatically. Higher leverage means higher potential profit but also higher risk.
              </div>

              <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"10px", marginBottom:"16px"}}>
                {LEVERAGE_OPTIONS.map(l => (
                  <div
                    key={l.value}
                    onClick={() => setSelectedLeverage(l.value)}
                    style={{
                      padding:"14px", textAlign:"center", borderRadius:"10px", cursor:"pointer",
                      border: selectedLeverage === l.value ? "2px solid #F0A500" : "1px solid #1E2840",
                      background: selectedLeverage === l.value ? "rgba(240,165,0,0.1)" : "#0B0E14",
                      transition:"all .15s"
                    }}
                  >
                    <div style={{fontSize:"16px", fontWeight:"700", color: selectedLeverage === l.value ? "#F0A500" : "#E8EAF0"}}>{l.label}</div>
                    <div style={{fontSize:"10px", color:"#5A6480", marginTop:"4px"}}>
                      {l.value === 99999 ? "Max position" : `1 USD = $${l.value} position`}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{background:"#0B0E14", borderRadius:"8px", padding:"12px", marginBottom:"14px"}}>
                <div style={{fontSize:"11px", color:"#5A6480", marginBottom:"6px", textTransform:"uppercase", letterSpacing:"1px"}}>Current Setting</div>
                <div style={{fontSize:"18px", fontWeight:"700", color:"#F0A500"}}>
                  {LEVERAGE_OPTIONS.find(l => l.value === selectedLeverage)?.label}
                </div>
                <div style={{fontSize:"12px", color:"#5A6480", marginTop:"4px"}}>
                  Example: 0.01 lot Gold @ $2300 = Position $
                  {(0.01 * 100 * 2300 / selectedLeverage).toFixed(2)} margin required
                </div>
              </div>

              <div style={{background:"rgba(255,59,92,0.06)", border:"1px solid rgba(255,59,92,0.15)", borderRadius:"8px", padding:"10px 12px", marginBottom:"14px", fontSize:"11px", color:"#FF3B5C"}}>
                ⚠️ Higher leverage increases both potential profit and potential loss. Use with caution.
              </div>

              {msg && (
                <div className="dep-msg" style={{color: msg.includes("✓") ? "#00C896" : "#FF3B5C", background: msg.includes("✓") ? "rgba(0,200,150,0.08)" : "rgba(255,59,92,0.08)"}}>
                  {msg}
                </div>
              )}

              <button className="btn-main" onClick={handleSaveLeverage} disabled={savingLeverage}>
                {savingLeverage ? "Saving..." : "Save Leverage Setting"}
              </button>
            </div>
          </div>
        )}

        {tab === "account" && (
          <div className="settings-grid">
            <div className="settings-card">
              <div className="settings-card-title">🔒 Change Password</div>
              <label className="dep-label">Current Password</label>
              <input className="inp" type="password" placeholder="Enter current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
              <label className="dep-label">New Password</label>
              <input className="inp" type="password" placeholder="Min 6 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              <label className="dep-label">Confirm New Password</label>
              <input className="inp" type="password" placeholder="Repeat new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
              {msg && tab === "account" && (
                <div className="dep-msg" style={{color: msg.includes("✓") ? "#00C896" : "#FF3B5C", background: msg.includes("✓") ? "rgba(0,200,150,0.08)" : "rgba(255,59,92,0.08)"}}>
                  {msg}
                </div>
              )}
              <button className="btn-main" onClick={handleChangePassword} disabled={loading}>
                {loading ? "Updating..." : "Change Password"}
              </button>
            </div>

            <div className="settings-card">
              <div className="settings-card-title">📧 Change Email</div>
              <div style={{fontSize:"12px", color:"#5A6480", marginBottom:"8px"}}>
                Current email: <strong style={{color:"#F0A500"}}>{user.email}</strong>
              </div>
              <label className="dep-label">New Email</label>
              <input className="inp" type="email" placeholder="Enter new email" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
              <label className="dep-label">Current Password</label>
              <input className="inp" type="password" placeholder="Confirm your password" value={emailPassword} onChange={e => setEmailPassword(e.target.value)} />
              <button className="btn-main" onClick={handleChangeEmail} disabled={loading} style={{marginTop:"8px"}}>
                {loading ? "Updating..." : "Change Email"}
              </button>
              <div className="settings-card-title" style={{marginTop:"20px"}}>ℹ️ Account Info</div>
              <div className="settings-info-row"><span>Email</span><span style={{color:"#F0A500"}}>{user.email}</span></div>
              <div className="settings-info-row"><span>User ID</span><span style={{fontFamily:"monospace", fontSize:"11px", color:"#5A6480"}}>{user.uid?.slice(0,16)}...</span></div>
              <div className="settings-info-row"><span>Status</span><span style={{padding:"2px 8px", borderRadius:"4px", fontSize:"10px", fontWeight:"700", background:"rgba(0,200,150,0.12)", color:"#00C896"}}>Active</span></div>
            </div>
          </div>
        )}

        {tab === "faq" && (
          <div className="faq-wrap">
            <div style={{fontSize:"13px", color:"#5A6480", marginBottom:"20px"}}>
              Find answers to the most common questions about Troy Trade Investment.
            </div>
            {FAQ.map((item, i) => (
              <div key={i} className="faq-item" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <div className="faq-question">
                  <span>{item.q}</span>
                  <span style={{color:"#F0A500", fontSize:"16px"}}>{openFaq === i ? "−" : "+"}</span>
                </div>
                {openFaq === i && <div className="faq-answer">{item.a}</div>}
              </div>
            ))}
          </div>
        )}

        {tab === "support" && (
          <div className="support-wrap">
            <div className="support-info-cards">
              <div className="support-info-card">
                <div className="support-info-icon">📧</div>
                <div className="support-info-label">Email Support</div>
                <div className="support-info-value">ptartemisbosphoraarthagena@gmail.com</div>
                <div className="support-info-note">Response within 24 hours</div>
              </div>
              <div className="support-info-card">
                <div className="support-info-icon">🌐</div>
                <div className="support-info-label">Website</div>
                <div className="support-info-value">artemisbosphora.com</div>
                <div className="support-info-note">Official company website</div>
              </div>
              <div className="support-info-card">
                <div className="support-info-icon">🏢</div>
                <div className="support-info-label">Office</div>
                <div className="support-info-value">House of Francis, Room 302</div>
                <div className="support-info-note">Ile Du Port, Mahe, Seychelles</div>
              </div>
            </div>
            {!supportSent ? (
              <div className="support-form">
                <div className="settings-card-title">Send Support Ticket</div>
                <label className="dep-label">Your Name</label>
                <input className="inp" placeholder="Full name" value={supportName} onChange={e => setSupportName(e.target.value)} />
                <label className="dep-label">Your Email</label>
                <input className="inp" placeholder="Email" value={supportEmail} onChange={e => setSupportEmail(e.target.value)} />
                <label className="dep-label">Subject</label>
                <input className="inp" placeholder="e.g. Deposit issue, Withdrawal question..." value={supportSubject} onChange={e => setSupportSubject(e.target.value)} />
                <label className="dep-label">Message</label>
                <textarea className="inp" placeholder="Describe your issue in detail..." value={supportMsg} onChange={e => setSupportMsg(e.target.value)} style={{minHeight:"120px", resize:"vertical"}} />
                {msg && <div className="dep-msg" style={{color: msg.includes("✓") ? "#00C896" : "#FF3B5C", background: msg.includes("✓") ? "rgba(0,200,150,0.08)" : "rgba(255,59,92,0.08)"}}>{msg}</div>}
                <button className="btn-main btn-full" onClick={handleSupportSubmit}>Send Support Ticket</button>
              </div>
            ) : (
              <div style={{textAlign:"center", padding:"40px", color:"#00C896"}}>
                <div style={{fontSize:"40px", marginBottom:"16px"}}>✓</div>
                <div style={{fontSize:"16px", fontWeight:"700"}}>Ticket Sent!</div>
                <div style={{fontSize:"13px", color:"#5A6480", marginTop:"8px"}}>We'll get back to you within 24 hours.</div>
                <button className="btn-main" style={{marginTop:"20px", padding:"10px 24px"}} onClick={() => { setSupportSent(false); setSupportSubject(""); setSupportMsg(""); setMsg(""); }}>Send Another</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}